import { useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { apiFetch } from "../../../app/apiClient";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ComponentLoading from "../../../components/ComponentLoading";
import { currentToken } from "../../login/loginSlice";
import { activeFolder, composeEmail, folderRefreshNonce, loadEmail } from "../emailSlice";
import EmailMessageItem from "./EmailMessageItem";

export interface EmailMessageType {
    key: string;
    from: string;
    subject: string;
    date: string;
}

async function openDraftForEdit(draftKey: string, dispatch: any, userToken: string | null | undefined) {
    try {
        const response = await apiFetch("/api/email/drafts/" + draftKey, {
            method: "GET",
            token: userToken
        });
        const data = await response.json();
        dispatch(composeEmail({
            guid: uuid(),
            draftKey: draftKey,
            to: data.email.to?.text || "",
            subject: data.email.subject || "",
            body: data.email.body?.html || data.email.body?.text || "",
            header: "Edit Draft"
        }));
    } catch (error: any) {
        if (error.name !== "SessionExpiredError") {
            throw error;
        }
    }
}

function populateMessages(messages: EmailMessageType[], dispatch: any, targetFolder: string | null, userToken: string | null | undefined) {
    return <>{
        messages.map((message) => {
            const onClick = targetFolder === "drafts"
                ? () => { openDraftForEdit(message.key, dispatch, userToken); }
                : () => { dispatch(loadEmail(message.key)); };
            return <EmailMessageItem message={message} key={message.key} onClick={onClick} />
        })
    }</>;
}

function FolderContents() {
    let dispatch = useAppDispatch();
    let [contents, setContents] = useState(<span>Loading...</span>);
    let [currentLoadingEvents, setCurrentLoadingEvents] = useState(0);
    let targetFolder = useAppSelector(activeFolder);
    let userToken = useAppSelector(currentToken);
    let refreshNonce = useAppSelector(folderRefreshNonce);
    // Tracks which folder's data (and refresh generation, and active account) is currently
    // rendered, so the effect only refetches when one of those actually changes, not on every
    // render. userToken is part of the key so switching the active account re-fetches the same
    // folder name instead of showing the previous account's already-loaded contents.
    let loadedFolderRef = useRef<string | null>(null);

    useEffect(() => {
        const cacheKey = targetFolder + ":" + refreshNonce + ":" + userToken;
        if (loadedFolderRef.current === cacheKey) {
            return;
        }
        const abortController = new AbortController();

        async function loadFolderData() {
            setCurrentLoadingEvents((count) => count + 1);
            setContents(<span>Loading...</span>);
            try {
                const response = await apiFetch("/api/emails/" + targetFolder, {
                    method: "GET",
                    token: userToken,
                    signal: abortController.signal
                });
                const folderData = await response.json();
                loadedFolderRef.current = cacheKey;
                if (folderData.messages.length > 0) {
                    setContents(populateMessages(folderData.messages, dispatch, targetFolder, userToken));
                } else {
                    setContents(<span>Empty...</span>);
                }
            } catch (error: any) {
                if (error.name !== "AbortError" && error.name !== "SessionExpiredError") {
                    setContents(<span>Failed to load folder.</span>);
                }
            } finally {
                setCurrentLoadingEvents((count) => count - 1);
            }
        }

        loadFolderData();
        return () => abortController.abort();
    }, [targetFolder, refreshNonce, userToken, dispatch]);

    return(<div className={"folderContents"}>
        <ComponentLoading isActive={currentLoadingEvents > 0} />
        <h2>{targetFolder?.toUpperCase()}</h2>
        {contents}
    </div>);
}

export default FolderContents;
